/**
 * Container for the filter chip component
 *
 * Accepts controlled open/close state from the parent to ensure
 * only one filter dropdown is open at a time. Pre-builds stable
 * option toggle handlers and manages dropdown search filtering.
 * When an optional onServerSearch callback is provided, search text
 * changes are debounced (300ms) and forwarded to the server while
 * client-side filtering provides immediate visual feedback.
 * On web, measures the trigger element's viewport position so the
 * dropdown renders with `position: fixed` at the correct coordinates.
 * A scroll/resize listener continuously re-measures the position so
 * the dropdown follows the trigger when the user scrolls the parent
 * container (DrawerBody, ModalBody, etc.).
 * The dropdown is rendered via createPortal into document.body to
 * escape CSS transform containing blocks created by Modal/Drawer
 * animations — without the portal, `position: fixed` would be
 * relative to the nearest transformed ancestor instead of the viewport.
 * Search input uses document-level keydown capture instead of focus
 * because the portal renders outside the dialog's focus trap, making
 * the input unfocusable. Keystrokes are intercepted globally and
 * forwarded to the search state while the input displays visually.
 * @module features/scouting/components/FilterChip/FilterChipContainer
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Platform } from "react-native";
import { useDebouncedCallback } from "use-debounce";

import FilterChipView, {
  panelMessageFor,
  CHIP_PANEL_MAX_HEIGHT,
  type FilterChipPlacement,
  type FilterOption,
} from "./FilterChipView";

import { FixedPositionDropdownShellPanel } from "@/components/atoms/FixedPositionDropdownShell";
import useDropdownPositioning from "@/components/atoms/FixedPositionDropdownShell/useDropdownPositioning";
import useClickOutside from "@/hooks/useClickOutside";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Combined selector for trigger wrapper + portal'd dropdown. Both elements
 * carry data-chip-dropdown="true" so the click-outside check returns "inside"
 * for either the trigger or the dropdown body. Without including the trigger,
 * the toggle-button click itself would race with the close handler.
 */
const DROPDOWN_SELECTOR = "[data-chip-dropdown]";

/** Measured trigger rectangle for fixed-position dropdown on web */
interface TriggerRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/** Max items to show as comma-separated before switching to count */
const MAX_VISIBLE_LABELS = 1;

/** Props for FilterChipContainer */
interface FilterChipContainerProps {
  /** Label prefix (e.g. "Position", "Club") */
  readonly label: string;
  /** Available options for the dropdown */
  readonly options: readonly FilterOption[];
  /** Currently selected option IDs */
  readonly selectedIds: readonly string[];
  /** Callback when selection changes — receives the toggled option ID */
  readonly onToggleOption: (optionId: string) => void;
  /** Callback to clear all selections (select "All") */
  readonly onClearAll: () => void;
  /** Whether this chip's dropdown is currently open (controlled by parent) */
  readonly isOpen: boolean;
  /** Callback to toggle this chip's dropdown */
  readonly onToggle: () => void;
  /** Callback to close this chip's dropdown */
  readonly onClose: () => void;
  /** Disables the chip while options are still loading */
  readonly loading?: boolean;
  /** Optional server-side search callback — debounced at 300ms */
  readonly onServerSearch?: (text: string) => void;
  /** Whether server-side search is currently loading for this chip */
  readonly searching?: boolean;
  /**
   * Stem for this chip's test ids, when the label-derived default is wrong.
   *
   * The default reads `player-filter:<label>`, which is accurate on the
   * surfaces the chip was built for and a lie everywhere else — a fixture's
   * competition filter is not a player filter, and a test id that says it is
   * sends the next reader to the wrong screen.
   */
  readonly testID?: string;
}

/**
 * Manages filter chip presentation, search, and pre-builds option toggle handlers
 * @param root0 - Component props
 * @param root0.label - Label prefix
 * @param root0.options - Available options
 * @param root0.selectedIds - Currently selected option IDs
 * @param root0.onToggleOption - Callback when an option is toggled
 * @param root0.onClearAll - Callback to clear all selections
 * @param root0.isOpen - Whether dropdown is open (controlled)
 * @param root0.onToggle - Toggle dropdown visibility
 * @param root0.onClose - Close dropdown
 * @param root0.loading - Whether options are still loading from the server
 * @param root0.onServerSearch - Optional server-side search callback, debounced at 300ms
 * @param root0.searching - Whether server-side search is loading for this chip
 * @param root0.testID - Stem for this chip's test ids
 */
const FilterChipContainer = ({
  label,
  options,
  selectedIds,
  onToggleOption,
  onClearAll,
  isOpen,
  onToggle,
  onClose,
  loading,
  onServerSearch,
  searching,
  testID,
}: FilterChipContainerProps) => {
  const isLoading = loading ?? false;
  const isWeb = Platform.OS === "web";
  const [searchText, setSearchText] = useState("");

  /** Debounced server-side search — fires 300ms after last keystroke */
  const debouncedServerSearch = useDebouncedCallback(
    (text: string) => onServerSearch?.(text),
    300
  );
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const triggerNodeRef = useRef<HTMLElement | null>(null);
  const [triggerRect, setTriggerRect] = useState<TriggerRect | null>(null);

  /** Callback ref — stores the DOM node for viewport measurement on web */
  const setTriggerNode = useCallback((node: unknown) => {
    triggerNodeRef.current = (node as HTMLElement) ?? null;
  }, []);

  /**
   * Native geometry, owned by the shell atom rather than re-derived here.
   *
   * It measures the chip, subtracts the keyboard from the viewport (the fix
   * earlier in this branch) and flips or clamps the panel accordingly, then
   * its `Panel` renders that geometry inside an RN Modal window. Reusing it is
   * what stops this molecule keeping a second, drifting copy of the same
   * decision — the shape that let `FilterChip` miss the keyboard entirely
   * while `useDropdownPositioning` already handled it.
   *
   * `triggerNodeRef` doubles as the anchor: on native it holds the RN node,
   * which is what `measureInWindow` needs.
   */
  const { panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen: isOpen && !isWeb,
    externalTriggerRef: triggerNodeRef as never,
    panelHeight: CHIP_PANEL_MAX_HEIGHT,
  });

  /**
   * Web keeps its own in-flow style; native hands position and size to the
   * shell's Panel, so the style function is told to describe the surface only.
   */
  const nativePlacement: FilterChipPlacement = isWeb ? "below" : "hosted";

  /**
   * Accumulated option labels across option list changes.
   * When server-side search resets, selected clubs may disappear from the
   * options array — this cache preserves their display labels for the chip.
   */
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});

  const selectedLabel = useMemo(() => {
    if (selectedIds.length === 0) return "All";
    if (selectedIds.length > MAX_VISIBLE_LABELS)
      return `${selectedIds.length} selected`;
    const labels = selectedIds.map(id => {
      const option = options.find(o => o.id === id);
      return option?.label ?? labelCache[id] ?? id;
    });
    return labels.join(", ");
  }, [options, selectedIds, labelCache]);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchText.toLowerCase().trim();
    return normalizedSearch === ""
      ? options
      : options.filter(o => o.label.toLowerCase().includes(normalizedSearch));
  }, [options, searchText]);

  const optionHandlers = useMemo(() => {
    const handlers = options.reduce(
      (acc, option) => ({
        ...acc,
        [option.id]: () => onToggleOption(option.id),
      }),
      {} as Record<string, () => void>
    );
    return { ...handlers, __all: onClearAll };
  }, [options, onToggleOption, onClearAll]);

  /** Wraps setSearchText to also trigger debounced server search */
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedServerSearch(text);
    },
    [debouncedServerSearch]
  );

  /** Clears search text, resets server search, and clears hidden input DOM value */
  const handleClearSearch = useCallback(() => {
    setSearchText("");
    debouncedServerSearch("");
    const el = hiddenInputRef.current;
    if (el) {
      el.value = "";
    }
  }, [debouncedServerSearch]);

  const handleToggle = useCallback(() => {
    handleClearSearch();
    onToggle();
  }, [onToggle, handleClearSearch]);

  const handleClose = useCallback(() => {
    setSearchText("");
    debouncedServerSearch("");
    onClose();
  }, [onClose, debouncedServerSearch]);

  /** Ref for the hidden input — unused on web but needed by the View contract */
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  /** No-op callback ref for the portal'd display-only search input */
  const setSearchInputNode = useCallback((_node: unknown) => {}, []);

  const theme = isDark ? "dark" : "light";

  /**
   * Wraps dropdown content in a portal to document.body on web.
   * The portal escapes CSS transform containing blocks from Modal/Drawer
   * animations that break position:fixed. Must portal to document.body
   * (not the dialog element) because GlueStack's Drawer uses CSS transforms
   * that make position:fixed relative to the dialog instead of the viewport.
   * On native, returns content unchanged.
   */
  /**
   * Where the open panel is rendered.
   *
   * WEB IS UNCHANGED — the document.body portal, with everything that hangs
   * off it (the fixed-position coordinates, the keydown capture, the focus
   * workarounds). None of it is touched here.
   *
   * NATIVE hosts the panel in the shell atom's `Panel`, which renders inside
   * its own transparent RN Modal window (SE-5568). That is the whole point:
   * an in-tree panel is clipped by whatever ancestor clips — in the Add match
   * modal, the surface's own ScrollView sheared off the panel's top, SEARCH
   * FIELD included, so a scout could not see what they were typing. A Modal
   * window has no such ancestor. The shell also brings the keyboard-aware
   * placement fixed earlier in this branch, so the flip is inherited rather
   * than re-implemented here.
   */
  const wrapDropdown = useCallback(
    (content: React.ReactNode): React.ReactNode => {
      // One binding, one return: the three shapes (portal, bare node, hosted
      // panel) are all ReactNode, and returning them from separate statements
      // reads to sonar as a function with several return types.
      const rendered: React.ReactNode = isWeb ? (
        typeof document === "undefined" ? (
          content
        ) : (
          createPortal(content, document.body)
        )
      ) : (
        <FixedPositionDropdownShellPanel
          geometry={panelGeometry}
          onDismiss={onClose}
        >
          {content}
        </FixedPositionDropdownShellPanel>
      );

      return rendered;
    },
    [isWeb, panelGeometry, onClose]
  );

  /** Reads the trigger's current viewport rect */
  const measureTrigger = useCallback((): TriggerRect | null => {
    const node = triggerNodeRef.current;
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  /* --- Side effects --- */

  /** Accumulate option labels so selected items survive option list changes */
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Accumulate labels from paginated search for stable chip display
    setLabelCache(prev =>
      options.reduce(
        (acc, o) => (acc[o.id] === o.label ? acc : { ...acc, [o.id]: o.label }),
        prev
      )
    );
  }, [options]);

  /**
   * Captures keystrokes at the document level when the dropdown is open.
   * The portal renders at document.body to escape CSS transform containing
   * blocks, and the dialog's focus trap prevents focusing elements inside
   * the portal. Instead, we capture keydown events globally and build the
   * search text from them. Dead keys (accents) produce key="Dead" which
   * is ignored, and the composed character (e.g. "á") arrives as a
   * subsequent keydown with key.length === 1.
   *
   * CAPTURE phase, and the space bar is why. Opening the panel leaves the
   * trigger focused, and a focused button is activated by space — so a bubble
   * listener's `preventDefault` arrived after react-native-web's Pressable had
   * already fired `onPress`, and the panel slammed shut on the first space.
   * Typing "Premier League" got as far as "Premier". Capture beats the
   * Pressable's own handler, which is the same reason the Escape listener
   * below is in capture phase.
   */
  useEffect(() => {
    if (!isOpen || !isWeb) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      /** Ignore modifier-only keys and navigation keys */
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape" || e.key === "Dead") return;

      if (e.key === "Backspace") {
        handleSearchChange(searchText.slice(0, -1));
        e.preventDefault();
        return;
      }

      /** Only accept printable single characters (including composed accents) */
      if (e.key.length === 1) {
        handleSearchChange(searchText + e.key);
        // Stops the focused trigger from ALSO seeing this key: space would
        // toggle the panel closed, and the character would be lost.
        e.stopPropagation();
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true }); // NOSONAR — intentional global keydown capture for portal'd search input outside dialog focus trap
    return () =>
      document.removeEventListener("keydown", handleKeyDown, {
        capture: true,
      });
  }, [isOpen, isWeb, searchText, handleSearchChange]);

  /** Synchronous initial measurement — prevents position flicker on open */
  useLayoutEffect(() => {
    if (isOpen && isWeb) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- DOM measurement requires useLayoutEffect
      setTriggerRect(measureTrigger());
    } else {
      setTriggerRect(null);
    }
  }, [isOpen, isWeb, measureTrigger]);

  /**
   * Keeps the dropdown aligned with the trigger while the user scrolls.
   * Listens in capture phase to catch scroll events from any ancestor
   * (DrawerBody, ModalBody, etc.), not just the window.
   */
  useEffect(() => {
    if (!isOpen || !isWeb) return undefined;

    const handleScroll = () => {
      setTriggerRect(measureTrigger());
    };

    window.addEventListener("scroll", handleScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen, isWeb, measureTrigger]);

  /**
   * SE-4615 — close the dropdown when the user clicks anywhere outside the
   * trigger wrapper or the portal'd dropdown. Both elements carry
   * data-chip-dropdown="true" so the selector covers either path; a naive
   * trigger-element-bounds check would close on every option click because
   * the portal renders outside the trigger's DOM subtree.
   */
  // FilterChip opens on pointer-up; pointer-down outside detection cannot race
  // with the opening gesture's later click event. Other dropdowns retain the
  // shared hook's click default for keyboard activation semantics.
  useClickOutside(isOpen, DROPDOWN_SELECTOR, handleClose, "pointerdown");

  /**
   * SE-4615 — Escape closes only the open chip dropdown and never bubbles up
   * to the host Modal. GlueStack's Modal listens for Escape via a `keyup`
   * handler on `document.body` (see `useBackHandler` in
   * `@gluestack-ui/utils`). Listening on `document` in capture phase fires
   * BEFORE `document.body` reaches target phase, so stopImmediatePropagation
   * here prevents the modal listener from receiving the event at all.
   *
   * Subtlety: closing the chip on keydown immediately unmounts this effect,
   * leaving the keyup unguarded — GlueStack's body listener then sees keyup
   * and tears down the parent modal. We instead suppress both keydown AND
   * keyup, and defer the actual chip close to the keyup phase so the
   * listener is still alive to swallow that keyup.
   */
  useEffect(() => {
    if (!isOpen || !isWeb) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      if (e.type === "keyup") handleClose();
    };

    document.addEventListener("keydown", handleEscape, { capture: true }); // NOSONAR — capture-phase intentional to pre-empt Modal's own Escape handler
    document.addEventListener("keyup", handleEscape, { capture: true }); // NOSONAR — must intercept keyup too; GlueStack's body-level useBackHandler listens on keyup
    return () => {
      document.removeEventListener("keydown", handleEscape, { capture: true });
      document.removeEventListener("keyup", handleEscape, { capture: true });
    };
  }, [isOpen, isWeb, handleClose]);

  return (
    <FilterChipView
      label={label}
      selectedLabel={selectedLabel}
      isOpen={isWeb ? isOpen : shouldShow}
      onToggle={handleToggle}
      onClose={handleClose}
      options={filteredOptions}
      selectedIds={selectedIds}
      optionHandlers={optionHandlers}
      searchText={searchText}
      onSearchChange={handleSearchChange}
      onClearSearch={handleClearSearch}
      searchInputRef={setSearchInputNode}
      loading={isLoading}
      panelMessage={panelMessageFor(searching ?? false, filteredOptions.length)}
      hiddenInputRef={hiddenInputRef}
      theme={theme}
      triggerRef={setTriggerNode}
      triggerRect={triggerRect}
      placement={nativePlacement}
      wrapDropdown={wrapDropdown}
      testID={testID}
    />
  );
};

export default FilterChipContainer;
